import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search, FileText, Loader, Calendar, Trash2, X, AlertCircle,
  ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { reportApi, Report } from '../services/reportApi';
import { caseApi } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { RedactionText } from '../components/redaction/RedactionText';

type SortKey = 'title' | 'caseName' | 'patientName' | 'status' | 'createdAt' | 'updatedAt';
type SortDirection = 'asc' | 'desc';
type StatusFilter = 'all' | 'complete' | 'archived';

const COLUMNS: { key: SortKey | 'actions'; label: string; sortable: boolean }[] = [
  { key: 'title',       label: 'Title',        sortable: true },
  { key: 'caseName',    label: 'Case',         sortable: true },
  { key: 'patientName', label: 'Patient',       sortable: true },
  { key: 'patientIdentifier' as any, label: 'Patient No.', sortable: false },
  { key: 'status',      label: 'Status',        sortable: true },
  { key: 'createdAt',   label: 'Created',       sortable: true },
  { key: 'updatedAt',   label: 'Updated',       sortable: true },
  { key: 'actions',     label: '',              sortable: false },
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'generating':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
    case 'failed':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    case 'archived':
      return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
    default:
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'generating': return 'Generating\u2026';
    case 'failed': return 'Failed';
    case 'archived': return 'Archived';
    default: return 'Complete';
  }
};

interface EnrichedReport extends Report {
  caseName: string;
  patientName: string;
  patientIdentifier: string;
}

export const ReportListPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortKey>('updatedAt');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [deleteModalReport, setDeleteModalReport] = useState<EnrichedReport | null>(null);
  const [confirmationText, setConfirmationText] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
    setCurrentPage(0);
  };

  // Fetch all reports
  const { data: reports, isLoading: reportsLoading, error: reportsError } = useQuery(
    ['reports'],
    () => reportApi.getUserReports(),
    { staleTime: 30000 }
  );

  // Extract unique caseIds and fetch case data
  const caseIds = useMemo(
    () => [...new Set((reports || []).map(r => r.caseId))],
    [reports]
  );

  const { data: caseMap } = useQuery(
    ['report-cases', caseIds.join(',')],
    async () => {
      const entries = await Promise.all(
        caseIds.map(async (id) => {
          try {
            const c = await caseApi.getCase(id);
            return [id, { name: c.name || 'Untitled Case', patientName: c.patientName || '', patientIdentifier: c.patientIdentifier || '' }] as const;
          } catch {
            return [id, { name: 'Unknown Case', patientName: '', patientIdentifier: '' }] as const;
          }
        })
      );
      return Object.fromEntries(entries) as Record<string, { name: string; patientName: string; patientIdentifier: string }>;
    },
    { enabled: caseIds.length > 0, staleTime: 60000 }
  );

  // Enrich, filter, sort, paginate
  const enrichedReports: EnrichedReport[] = useMemo(() => {
    if (!reports) return [];
    return reports.map(r => ({
      ...r,
      caseName: caseMap?.[r.caseId]?.name || 'Loading...',
      patientName: caseMap?.[r.caseId]?.patientName || '',
      patientIdentifier: caseMap?.[r.caseId]?.patientIdentifier || '',
    }));
  }, [reports, caseMap]);

  const filtered = useMemo(() => {
    let result = enrichedReports;
    if (statusFilter !== 'all') {
      result = result.filter(r => r.status === statusFilter);
    }
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(r =>
        r.title.toLowerCase().includes(lower) ||
        r.caseName.toLowerCase().includes(lower) ||
        r.patientName.toLowerCase().includes(lower)
      );
    }
    return result;
  }, [enrichedReports, searchTerm, statusFilter]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av: string, bv: string;
      switch (sortBy) {
        case 'title':       av = a.title;       bv = b.title;       break;
        case 'caseName':    av = a.caseName;    bv = b.caseName;    break;
        case 'patientName': av = a.patientName; bv = b.patientName; break;
        case 'status':      av = a.status;      bv = b.status;      break;
        case 'createdAt':   av = a.createdAt;   bv = b.createdAt;   break;
        case 'updatedAt':   av = a.updatedAt;   bv = b.updatedAt;   break;
        default: return 0;
      }
      const cmp = av.localeCompare(bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paginated = sorted.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  // Delete
  const deleteMutation = useMutation(
    (reportId: string) => reportApi.deleteReport(reportId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['reports']);
        queryClient.invalidateQueries('recentReports');
        setDeleteModalReport(null);
        setConfirmationText('');
      },
      onError: () => {
        alert('Failed to delete report. Please try again.');
      },
    }
  );

  const handleDeleteClick = (e: React.MouseEvent, report: EnrichedReport) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteModalReport(report);
  };

  const handleConfirmDelete = () => {
    if (deleteModalReport && confirmationText === deleteModalReport.title) {
      deleteMutation.mutate(deleteModalReport.id);
    }
  };

  const SortIcon: React.FC<{ columnKey: string }> = ({ columnKey }) => {
    if (sortBy !== columnKey) {
      return <ChevronsUpDown className="h-3.5 w-3.5 text-medical-400" />;
    }
    return sortDir === 'asc'
      ? <ChevronUp className="h-3.5 w-3.5 text-primary-600" />
      : <ChevronDown className="h-3.5 w-3.5 text-primary-600" />;
  };

  if (reportsLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-2 text-medical-600 dark:text-medical-400">Loading reports...</span>
        </div>
      </div>
    );
  }

  if (reportsError) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-2">Error Loading Reports</h3>
            <p className="text-medical-600 dark:text-medical-400">Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-medical-900 dark:text-medical-100">Drafts</h1>
        <p className="text-medical-600 dark:text-medical-400">View and manage your generated medical-legal drafts</p>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-medical-400 dark:text-medical-500" />
              <input
                type="text"
                placeholder="Search by title, case, or patient..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as StatusFilter); setCurrentPage(0); }}
              className="input-field"
            >
              <option value="all">All Status</option>
              <option value="complete">Complete</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </div>

      {/* Drafts Table */}
      {sorted.length === 0 ? (
        <div className="card">
          <div className="text-center py-12">
            <FileText className="h-16 w-16 text-medical-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-2">No Drafts Yet</h3>
            <p className="text-medical-600 dark:text-medical-400 mb-6">
              {searchTerm || statusFilter !== 'all'
                ? 'No drafts match your current filters.'
                : 'Start your first draft to get started.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="card">
          {/* Table header bar */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-medical-500">
              {sorted.length} {sorted.length === 1 ? 'report' : 'reports'}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-medical-500">Rows:</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(0); }}
                className="input-field w-20 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto -mx-6">
            <table className="min-w-full divide-y divide-medical-200 dark:divide-medical-700">
              <thead className="bg-medical-50 dark:bg-medical-900">
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => col.sortable && toggleSort(col.key as SortKey)}
                      className={`px-4 py-3 text-left text-xs font-medium text-medical-500 uppercase tracking-wider ${col.sortable ? 'cursor-pointer hover:text-medical-700 dark:hover:text-medical-300 select-none' : ''}`}
                    >
                      <div className="flex items-center gap-1">
                        <span>{col.label}</span>
                        {col.sortable && <SortIcon columnKey={col.key} />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-medical-800 divide-y divide-medical-200 dark:divide-medical-700">
                {paginated.map((report) => (
                  <tr
                    key={report.id}
                    className="hover:bg-medical-50 dark:hover:bg-medical-700/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/cases/${report.caseId}/overview`, { state: { openReportId: report.id } })}
                  >
                    {/* Title */}
                    <td className="px-4 py-3 max-w-[280px]">
                      <span
                        className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 block truncate"
                        title={report.title}
                      >
                        {report.title}
                      </span>
                    </td>

                    {/* Case */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Link
                        to={`/cases/${report.caseId}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                      >
                        <RedactionText text={report.caseName} />
                      </Link>
                    </td>

                    {/* Patient */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-medical-700 dark:text-medical-300">
                      {report.patientName ? (
                        <RedactionText text={report.patientName} />
                      ) : (
                        <span className="text-medical-400">&mdash;</span>
                      )}
                    </td>

                    {/* Patient Identifier */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-medical-700 dark:text-medical-300">
                      {report.patientIdentifier ? (
                        <RedactionText text={report.patientIdentifier} />
                      ) : (
                        <span className="text-medical-400">&mdash;</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                        {getStatusLabel(report.status)}
                      </span>
                    </td>

                    {/* Created */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-medical-500 dark:text-medical-400">
                      {formatDistanceToNow(new Date(report.createdAt), { addSuffix: true })}
                    </td>

                    {/* Updated */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-medical-500 dark:text-medical-400">
                      {report.updatedAt !== report.createdAt
                        ? formatDistanceToNow(new Date(report.updatedAt), { addSuffix: true })
                        : <span className="text-medical-400">&mdash;</span>}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <button
                        onClick={(e) => handleDeleteClick(e, report)}
                        className="p-1.5 text-gray-400 dark:text-medical-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Delete report"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-medical-200 dark:border-medical-700">
              <p className="text-sm text-medical-500">
                Page {currentPage + 1} of {totalPages}
              </p>
              <div className="flex gap-1">
                <button
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="p-2 rounded-lg text-medical-600 dark:text-medical-400 hover:bg-medical-100 dark:hover:bg-medical-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  disabled={currentPage >= totalPages - 1}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="p-2 rounded-lg text-medical-600 dark:text-medical-400 hover:bg-medical-100 dark:hover:bg-medical-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-medical-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-medical-100">Delete Draft</h3>
                  <p className="text-sm text-gray-600 dark:text-medical-400">This action cannot be undone</p>
                </div>
              </div>
              <button
                onClick={() => { setDeleteModalReport(null); setConfirmationText(''); }}
                className="text-gray-400 dark:text-medical-500 hover:text-gray-600 dark:hover:text-medical-300 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 dark:text-medical-300 mb-4">
                You are about to permanently delete:
              </p>
              <div className="bg-gray-50 dark:bg-medical-700 border border-gray-200 dark:border-medical-600 rounded-lg p-3 mb-4">
                <p className="font-medium text-gray-900 dark:text-medical-100">{deleteModalReport.title}</p>
                <p className="text-sm text-gray-600 dark:text-medical-400 mt-1">
                  Case: {deleteModalReport.caseName}
                </p>
              </div>
              <p className="text-sm text-gray-700 dark:text-medical-300 mb-3">
                To confirm, type the draft title exactly:
              </p>
              <input
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="Type draft title here"
                className="w-full px-3 py-2 border border-gray-300 dark:border-medical-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-medical-700 dark:text-medical-100"
                autoFocus
              />
              {confirmationText && confirmationText !== deleteModalReport.title && (
                <p className="text-sm text-red-600 mt-2">Title does not match</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setDeleteModalReport(null); setConfirmationText(''); }}
                disabled={deleteMutation.isLoading}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-medical-600 text-gray-700 dark:text-medical-300 rounded-lg hover:bg-gray-50 dark:hover:bg-medical-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={confirmationText !== deleteModalReport.title || deleteMutation.isLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {deleteMutation.isLoading ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Delete Draft</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
